import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg';
import { ReactComponent as IconMenu } from '@/assets/svgs/menu-dark.svg';
import SortIcon from '@/assets/svgs/sort-icon.svg';
import SortIconActive from '@/assets/svgs/sort-icon-active.svg';
import SortIconActiveUp from '@/assets/svgs/sort-icon-active-up.svg';
import MenuDropDown from '@/components/MenuDropDown/index';
import getPageLabel from '@/hooks/use-page-details/index';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

const getSortIconSrc = (sortKey, columnKey, sortDirection) => {
	if (sortKey !== columnKey) return SortIcon;
	return sortDirection === 'desc' ? SortIconActive : SortIconActiveUp;
};

/**
 * DataTable — generic listing table with sortable headers, per-row actions,
 * loading / error / empty states, and optional pagination.
 *
 * Visuals use the global `data-table__table__*` classes from
 * `_data-table.scss`.
 */
const DataTable = ({
	columns,
	data,
	getRowKey,
	onRowClick,
	rowActions,
	getRowProps,
	sortKey,
	sortDirection = 'asc',
	onSort,
	loading = false,
	loadingContent = 'Loading...',
	error,
	emptyMessage = 'No data available.',
	emptyIcon,
	pagination,
	className,
}) => {
	const hasActions = typeof rowActions === 'function';

	const getSortClass = (col) => {
		if (sortKey !== col.key) return '';
		return sortDirection === 'asc' ? 'is-asc' : 'is-desc';
	};

	const handleHeaderClick = (col) => {
		if (!col.sortable || !onSort) return;
		onSort(col.key);
	};

	const handleHeaderKey = (e, col) => {
		if (!col.sortable) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleHeaderClick(col);
		}
	};

	const renderRow = (row, rowIdx) => {
		const key = getRowKey ? getRowKey(row, rowIdx) : rowIdx;
		const actions = hasActions ? (rowActions(row) || []).filter(Boolean) : null;
		const isClickable = Boolean(onRowClick);
		const Wrapper = isClickable ? 'a' : 'div';
		const extraRowProps = typeof getRowProps === 'function' ? getRowProps(row, rowIdx) : {};
		const wrapperProps = isClickable
			? {
				href: '#',
				onClick: (e) => { e.preventDefault(); onRowClick(row); },
				className: 'data-table__table__body__row data-table__table__body__row--clickable',
				...extraRowProps,
			}
			: { className: 'data-table__table__body__row', ...extraRowProps };

		return (
			<Wrapper key={key} {...wrapperProps}>
				{columns.map((col) => (
					<div
						key={col.key}
						className={`data-table__table__body__content data-table__table__body__content--text ${col.cellClassName || ''}`}
					>
						{col.render ? col.render(row, rowIdx) : row[col.key]}
					</div>
				))}
				{hasActions && (
					<div
						className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--mc-end"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						{actions && actions.length > 0 && (
							<MenuDropDown
								button={() => (
									<span className="eclip-landing_cursor-mode__pointer eclip-table--menu">
										<IconMenu />
									</span>
								)}
								list={actions}
							/>
						)}
					</div>
				)}
			</Wrapper>
		);
	};

	const isEmpty = !loading && !error && data.length === 0;
	const hasRows = !loading && !error && data.length > 0;

	return (
		<div className={`data-table__table ${className || ''}`}>
			<div className="data-table__table__head__row">
				{columns.map((col) => (
					<div
						key={col.key}
						className={`data-table__table__head__title data-table__table__head__title__sort-order ${col.sortable ? 'link' : ''} ${getSortClass(col)} ${col.headerClassName || ''}`}
						role={col.sortable ? 'button' : undefined}
						tabIndex={col.sortable ? 0 : undefined}
						onClick={() => handleHeaderClick(col)}
						onKeyDown={(e) => handleHeaderKey(e, col)}
					>
						<div>{col.label}</div>
						{col.sortable && (
							<div className="etr-maker-checker-btn-group">
								<img
									src={getSortIconSrc(sortKey, col.key, sortDirection)}
									alt="Sort"
									className="sort-icon"
								/>
							</div>
						)}
					</div>
				))}
				{hasActions && (
					<div className="data-table__table__head__title data-table__table__head__title--mc-end" />
				)}
			</div>
			<div className="data-table__table__body">
				{loading && (
					<div className="data-table__table__body__content">
						<div className="data-table__table__body__content__button">
							<div className="data-table__center-text">{loadingContent}</div>
						</div>
					</div>
				)}
				{!loading && error && (
					<div className="data-table__table__body__content">
						<div className="data-table__table__body__content__button">
							<div className="data-table__center-text">{error}</div>
						</div>
					</div>
				)}
				{isEmpty && (
					<div className="data-table__empty">
						<div className="data-table__empty__icon">
							{emptyIcon}
							<span>{emptyMessage}</span>
						</div>
					</div>
				)}
				{hasRows && data.map(renderRow)}
			</div>
			{pagination && hasRows && <TableFooter {...pagination} />}
		</div>
	);
};

const TableFooter = ({ currentPage, totalPages = 1, totalEntries = 0, pageLimit = 10, onPageChange }) => {
	const [pageInput, setPageInput] = useState(String(currentPage));

	useEffect(() => {
		setPageInput(String(currentPage));
	}, [currentPage]);

	const goToPage = (p) => {
		const clamped = Math.max(1, Math.min(totalPages, p));
		if (clamped !== currentPage) onPageChange?.(clamped);
	};

	return (
		<div className="data-table__table__footer">
			<div className="data-table__table__footer__row">
				<div className="data-table__table__footer__row__left">
					<div className="data-table__table__meta">
						Showing {getPageLabel(totalEntries, pageLimit, currentPage - 1)} of {totalEntries} entries
					</div>
				</div>
				<div className="data-table__table__footer__row__right">
					<div className="data-table__table__pagination">
						<input
							className="data-table__table__pagination__page"
							type="number"
							value={pageInput}
							onChange={(e) => {
								setPageInput(e.target.value);
								const val = parseInt(e.target.value, 10);
								if (!isNaN(val) && val >= 1 && val <= totalPages) onPageChange?.(val);
							}}
							onBlur={() => setPageInput(String(currentPage))}
							min="1"
						/>
						<span className="data-table__table__pagination__page__meta">
							{' '}of {totalPages} pages{' '}
						</span>
						<button
							type="button"
							className="etr-maker-checker__data-table__table__pagination__prev"
							onClick={() => goToPage(currentPage - 1)}
							disabled={currentPage === 1}
						>
							<IconChevronDown />
						</button>
						<button
							type="button"
							className="etr-maker-checker__data-table__table__pagination__next"
							onClick={() => goToPage(currentPage + 1)}
							disabled={currentPage === totalPages}
						>
							<IconChevronDown />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

TableFooter.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number,
	totalEntries: PropTypes.number,
	pageLimit: PropTypes.number,
	onPageChange: PropTypes.func,
};

export default DataTable;

DataTable.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.node.isRequired,
		sortable: PropTypes.bool,
		render: PropTypes.func,
		headerClassName: PropTypes.string,
		cellClassName: PropTypes.string,
	})).isRequired,
	data: PropTypes.array.isRequired,
	getRowKey: PropTypes.func,
	onRowClick: PropTypes.func,
	rowActions: PropTypes.func,
	sortKey: PropTypes.string,
	sortDirection: PropTypes.oneOf(['asc', 'desc']),
	onSort: PropTypes.func,
	loading: PropTypes.bool,
	loadingContent: PropTypes.node,
	error: PropTypes.node,
	emptyMessage: PropTypes.node,
	emptyIcon: PropTypes.node,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number.isRequired,
		totalPages: PropTypes.number,
		totalEntries: PropTypes.number,
		pageLimit: PropTypes.number,
		onPageChange: PropTypes.func,
	}),
	className: PropTypes.string,
};
